import React from 'react';
import Navbar from '@/components/Navbar';
import CTASection from '@/components/CTASection';
import LeadgeneratieCTA from '@/components/LeadgeneratieCTA';
import { PhoneCall, Calendar, Database, BarChart3, FileText, PieChart, CheckCircle, ArrowRight, PhoneIncoming } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from 'react-router-dom';
import ServiceCard from '@/components/ServiceCard';
import PageSEO from '@/components/seo/PageSEO';

const structuredData = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "itemListElement": [
    {
      "@type": "Service",
      "position": 1,
      "name": "Telefonische prospectie",
      "description": "Specialisten bellen namens jouw bedrijf naar potentiële klanten, in jouw bedrijfstaal en met begrip van de Vlaamse markt",
      "provider": {
        "@type": "Organization",
        "name": "Afsprakenmaker"
      },
      "areaServed": "Belgium"
    },
    {
      "@type": "Service",
      "position": 2,
      "name": "Afspraken inplannen",
      "description": "Warme leads worden meteen ingepland in jouw agenda, op tijdstippen die voor jou werken",
      "provider": {
        "@type": "Organization",
        "name": "Afsprakenmaker"
      },
      "areaServed": "Belgium"
    },
    {
      "@type": "Service",
      "position": 3,
      "name": "CRM-integratie",
      "description": "Volledige synchronisatie met Podio of jouw eigen CRM-systeem",
      "provider": {
        "@type": "Organization",
        "name": "Afsprakenmaker"
      },
      "areaServed": "Belgium"
    }
  ]
};

const Diensten = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <PageSEO
        title="Outbound & Inbound Diensten | Professionele B2B Prospectie"
        description="OUTBOUND: Actief nieuwe klanten vinden. INBOUND: Alle oproepen omzetten naar afspraken. Professionele telefonische prospectie door ervaren specialisten."
        keywords="B2B telemarketing, afspraken maken, CRM-integratie, leadgeneratie KMO, telefonische prospectie, inbound callcenter, professionele prospectie"
        canonicalUrl="/diensten"
        structuredData={structuredData}
      />
      <Navbar />
      
      {/* Hero Section - Increased top padding */}
      <section className="bg-brand-lightBlue pt-32 pb-16 md:pt-36 md:pb-24 relative">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
            alt="Happy business professionals in a meeting" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Outbound & Inbound Afspraken voor jouw bedrijf
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Of je nu actief nieuwe klanten wilt bereiken of alle inkomende kansen wilt omzetten - wij zorgen voor afspraken die écht tellen. Professionele prospectie door ervaren specialisten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="#outbound" className="btn-primary inline-block">
                Bekijk OUTBOUND
              </Link>
              <Link to="#inbound" className="btn-outline inline-block">
                Bekijk INBOUND
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Telefonische Prospectie Section */}
      <section id="outbound" className="section scroll-mt-20">
        <div className="container">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-brand-lightBlue text-brand-blue px-4 py-2 rounded-full mb-4">
              <span className="font-bold">OUTBOUND</span>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Perfect voor B2B bedrijven die actief nieuwe klanten willen bereiken en verkoop afspraken willen inplannen voor hun team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand-lightRed text-brand-red px-4 py-2 rounded-full mb-4">
                <PhoneCall size={18} />
                <span className="font-medium">B2B Telefonische Prospectie</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Gerichte telefonische acquisitie naar jouw ideale klanten
              </h2>
              <p className="text-gray-700 mb-6">
                Onze telefonische prospectie is specifiek ontwikkeld voor B2B-bedrijven in België die consistent nieuwe klanten willen aantrekken zonder de typische verkooppraatjes die afschrikken.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Persoonlijke benadering die werkt</p>
                    <p className="text-gray-600">Natuurlijke gesprekken die aansluiten bij jouw bedrijfscultuur en waarden</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Vlaamse marktkennis</p>
                    <p className="text-gray-600">We kennen de lokale markt, cultuur en beslissingsprocessen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Volledig uitbesteed</p>
                    <p className="text-gray-600">Jij focust op je kernactiviteiten, wij zorgen voor de leads</p>
                  </div>
                </div>
              </div>
              
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Start met telefonische prospectie
                <ArrowRight size={18} />
              </Link>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Mensen in zakelijke gesprek met grote lach" 
                className="rounded-lg shadow-md w-full h-auto object-cover aspect-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Afspraken Inplannen Section */}
      <section className="bg-brand-gray section">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Business professionals scheduling appointments on a calendar" 
                className="rounded-lg shadow-md w-full h-auto object-cover aspect-video"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex items-center gap-2 bg-brand-lightRed text-brand-red px-4 py-2 rounded-full mb-4">
                <Calendar size={18} />
                <span className="font-medium">Afspraken Inplannen</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Je agenda gevuld met warme afspraken op het juiste moment
              </h2>
              <p className="text-gray-700 mb-6">
                Geen gedoe meer met agendabeheer. Wij zorgen ervoor dat geïnteresseerde prospects direct in jouw agenda worden ingepland, zodat je een constante stroom van kansen hebt.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Naadloze agendakoppeling</p>
                    <p className="text-gray-600">Directe integratie met Google Calendar, Outlook of jouw eigen systeem</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Slimme planningstool</p>
                    <p className="text-gray-600">Voorkom dubbele boekingen en respecteer jouw beschikbare tijdsvensters</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Gedetailleerde voorbereiding</p>
                    <p className="text-gray-600">Krijg vooraf alle info over de prospect zodat je perfect voorbereid bent</p>
                  </div>
                </div>
              </div>
              
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Vul jouw agenda met kwaliteitsvolle afspraken
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CRM Integratie Section */}
      <section className="section">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand-lightBlue text-brand-blue px-4 py-2 rounded-full mb-4">
                <Database size={18} />
                <span className="font-medium">CRM-Integratie</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Alle klantgegevens perfect georganiseerd in jouw CRM
              </h2>
              <p className="text-gray-700 mb-6">
                Vergeet handmatige data-invoer en verloren informatie. Onze integratie zorgt ervoor dat alle klantgegevens, interacties en notities automatisch in jouw CRM worden bijgehouden.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Volledige synchronisatie</p>
                    <p className="text-gray-600">Naadloze integratie met Podio, Salesforce, HubSpot en meer</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Rijke klantprofielen</p>
                    <p className="text-gray-600">Gedetailleerde gespreksnotities, voorkeuren en contactmomenten</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Business intelligence</p>
                    <p className="text-gray-600">Krijg inzicht in leadkwaliteit, conversieratio's en salesperformance</p>
                  </div>
                </div>
              </div>
              
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Optimaliseer jouw CRM-workflow
                <ArrowRight size={18} />
              </Link>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Vrolijke zakelijke bijeenkomst met handdruk" 
                className="rounded-lg shadow-md w-full h-auto object-cover aspect-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INBOUND Section */}
      <section id="inbound" className="bg-brand-gray section scroll-mt-20">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-brand-lightRed text-brand-red px-4 py-2 rounded-full mb-4">
              <span className="font-bold">INBOUND</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Zet alle inkomende oproepen om in afspraken
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto mb-8">
              Gemiste oproep = gemiste klant. In een tijd waarin klanten direct antwoord verwachten, kan één gemiste oproep het verschil maken tussen een nieuwe klant en een verloren kans. Onze INBOUND dienst zorgt dat je nooit meer een kans mist.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 bg-brand-lightRed text-brand-red px-4 py-2 rounded-full mb-4">
                <PhoneIncoming size={18} />
                <span className="font-medium">INBOUND Afspraken</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
                Het probleem dat je herkent
              </h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Gemiste oproepen = gemiste klanten</p>
                    <p className="text-gray-600 text-sm">Elke gemiste oproep is een potentiële klant die naar de concurrent gaat</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Druk = geen tijd om telefoon op te nemen</p>
                    <p className="text-gray-600 text-sm">Je zit in vergadering, bij een klant, of bent gewoon té druk</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <div className="text-brand-red">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Klanten verwachten direct antwoord</p>
                    <p className="text-gray-600 text-sm">In 2025 wil niemand meer wachten - ze bellen gewoon de volgende</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80" 
                alt="Customer service representative helping client" 
                className="rounded-lg shadow-md w-full h-auto object-cover aspect-video"
              />
            </div>
          </div>

          {/* INBOUND Benefits */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-center mb-8">Onze INBOUND oplossing</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-brand-red mb-4 flex justify-center">
                  <PhoneIncoming size={32} />
                </div>
                <h4 className="font-semibold mb-2">Wij nemen ALLE oproepen aan</h4>
                <p className="text-sm text-gray-600">24/7 of tijdens jouw kantooruren - jij kiest</p>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-brand-red mb-4 flex justify-center">
                  <CheckCircle size={32} />
                </div>
                <h4 className="font-semibold mb-2">Beantwoorden eerste vragen</h4>
                <p className="text-sm text-gray-600">FAQ's, prijzen, beschikbaarheid - wij weten het</p>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-brand-red mb-4 flex justify-center">
                  <Calendar size={32} />
                </div>
                <h4 className="font-semibold mb-2">Plannen afspraken direct in</h4>
                <p className="text-sm text-gray-600">Naadloze koppeling met jouw agenda</p>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="text-brand-red mb-4 flex justify-center">
                  <PhoneCall size={32} />
                </div>
                <h4 className="font-semibold mb-2">Nemen terugbel afspraken aan</h4>
                <p className="text-sm text-gray-600">Als het nu niet past, plannen we een geschikt moment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wat Mag Je Verwachten Section */}
      <section className="bg-brand-red section">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Wat mag je als B2B-organisatie verwachten?
            </h2>
            <p className="text-white/90 text-lg">
              Volledige transparantie en meetbare resultaten - dat zijn onze kernwaarden bij elk project.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-md transition-transform hover:translate-y-[-5px]">
              <div className="text-brand-red mb-4">
                <FileText size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Gedetailleerde rapportage</h3>
              <p className="text-gray-600 mb-6">
                Wekelijks ontvang je een uitgebreide rapportage over alle gesprekken, feedback en resultaten. Volledige inzage in wat er gebeurt.
              </p>
              <p className="text-brand-red font-medium text-sm">Gemiddeld 98% klanttevredenheid</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md transition-transform hover:translate-y-[-5px]">
              <div className="text-brand-red mb-4">
                <BarChart3 size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Strikte leadkwalificatie</h3>
              <p className="text-gray-600 mb-6">
                We zorgen dat je tijd optimaal wordt besteed. Alleen afspraken met leads die voldoen aan jouw specifieke criteria worden ingepland.
              </p>
              <p className="text-brand-red font-medium text-sm">+40% hogere conversieratio dan branchegemiddelde</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md transition-transform hover:translate-y-[-5px]">
              <div className="text-brand-red mb-4">
                <PieChart size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Strategische inzichten</h3>
              <p className="text-gray-600 mb-6">
                Leer van elke interactie. We delen waardevolle marktinzichten en feedback die we verzamelen tijdens de gesprekken.
              </p>
              <p className="text-brand-red font-medium text-sm">Continue optimalisatie van de aanpak</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-brand-gray">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Veelgestelde vragen</h2>
            <p className="section-subtitle mx-auto">
              Antwoorden op de meestgestelde vragen over onze diensten.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-left font-medium">
                  Wat is het verschil tussen OUTBOUND en INBOUND?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  OUTBOUND is actief nieuwe klanten benaderen: wij bellen namens jou naar potentiële klanten om verkoop afspraken in te plannen. INBOUND is reactief: wij nemen alle inkomende oproepen aan van geïnteresseerde klanten en zetten deze om in concrete afspraken. OUTBOUND is perfect voor B2B groei, INBOUND voor maximale bereikbaarheid en klanttevredenheid.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger className="text-left font-medium">
                  Kan ik INBOUND en OUTBOUND combineren?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Absoluut! Veel van onze klanten gebruiken OUTBOUND om actief nieuwe markten te betreden en INBOUND om alle leads die via hun website, advertenties of mond-tot-mond reclame binnenkomen optimaal te benutten. Dit geeft je een complete lead-generatie strategie zonder gemiste kansen.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-left font-medium">
                  Binnen welke termijn kan ik eerste resultaten verwachten?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Voor OUTBOUND: de eerste afspraken worden meestal binnen 2 weken na de start ingepland. Voor INBOUND: zodra de setup klaar is (ongeveer 1 week) kunnen we direct starten met het opnemen van oproepen. Onze klanten zien gemiddeld binnen 4-6 weken een significante impact op hun agenda en omzet.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger className="text-left font-medium">
                  Hoe waarborgen jullie de kwaliteit van de leads?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  We werken met een uitgebreide kwalificatieprocedure specifiek afgestemd op jouw ideale klantprofiel. Voordat een afspraak wordt ingepland, controleren we of de prospect aan minimaal 3 criteria voldoet: beslissingsbevoegdheid, budget en concrete behoefte. Daarnaast krijg je vooraf een gedetailleerde briefing over elke afspraak zodat je perfect voorbereid bent.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger className="text-left font-medium">
                  Hoe zorgen jullie dat de telefoongesprekken passen bij mijn bedrijfscultuur?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  We besteden uitgebreid tijd aan het leren kennen van jouw bedrijf, diensten en bedrijfscultuur. Onze specialisten worden zorgvuldig getraind in jouw specifieke taalgebruik, waardepropositie, FAQ's en tone-of-voice. We blijven dit verfijnen op basis van feedback.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6">
                <AccordionTrigger className="text-left font-medium">
                  Wat is de contractduur en opzegtermijn?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  We bieden flexibele contracten aan die maandelijks opzegbaar zijn. Voor een optimale strategie en meetbare resultaten raden we een minimale samenwerkingsperiode van 3 maanden aan, maar dit is geen verplichting. Tijdens het intakegesprek bespreken we graag de specifieke voorwaarden die het beste bij jouw situatie passen.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7">
                <AccordionTrigger className="text-left font-medium">
                  Ik gebruik een ander CRM-systeem dan Podio. Is dat een probleem?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700">
                  Niet in de meeste gevallen. We hebben ervaring met een breed scala aan CRM-systemen zoals Salesforce, HubSpot, Teamleader, en kunnen via API-koppelingen of webhooks integreren met nagenoeg elk populair systeem. Voor minder gangbare CRM-systemen bekijken we samen de mogelijkheden en zoeken we de meest praktische oplossing die past bij jouw werkwijze.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Extra Services CTA */}
      <section className="section bg-brand-lightBlue">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-title">Onze andere oplossingen</h2>
            <p className="section-subtitle mx-auto">
              Naast onze kerndiensten bieden we ook gespecialiseerde tools aan.
            </p>
          </div>
          <div className="grid md:grid-cols-1 max-w-md mx-auto gap-8">
            <ServiceCard 
              title="Telemarketing Calculator" 
              description="Bereken zelf wat een telemarketing campagne zou kosten voor jouw specifieke situatie." 
              icon={<BarChart3 size={32} />} 
              link="/calculator" 
            />
          </div>
        </div>
      </section>

      {/* Leadgeneratie CTA */}
      <LeadgeneratieCTA />
      
      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Diensten;